<!--
  Zimobi: the mobile bottom navigation. The v4 sidebar is `hidden sm:flex`, so below 640px this
  bar is the only navigation, and it is also the only way into the profile menu and the account
  switcher on a phone.

  Trap: `h-16` never applies. This is a flex item of the `flex-col` wrapper in Dashboard.vue,
  whose `<main>` is already `h-full`, so the bar is squeezed to the height of its own contents.
  The `my-2` on each slot is what sets the height — a 40px control plus 8px above and below,
  giving the same 56px row the v3 bar had. Drop the margins and the bar collapses to 40px.

  Ported from the v3 sidebar primitives (AgentDetails + OptionsMenu + PrimaryNavItem +
  default-sidebar), which Chatwoot deleted in v4.4.0. SidebarProfileMenu is rendered `compact`:
  the sidebar form is full width and prints the name and e-mail, which would eat this 64px row.
  Account switching lives inside that menu, as it did in the v3 OptionsMenu, because v4 moved it
  into SidebarAccountSwitcher — a full-width component with no place in this bar.
-->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import bottomTabBarMenuItems from './config/sidebarItems/bottomTabBarMenu';
import Icon from 'next/icon/Icon.vue';
import SidebarProfileMenu from 'next/sidebar/SidebarProfileMenu.vue';

const emit = defineEmits(['toggleAccounts', 'openKeyShortcutModal']);

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const menuItems = computed(() =>
  bottomTabBarMenuItems(accountId.value)
    .filter(item => {
      if (!item.roles.includes(currentRole.value)) return false;
      if (item.featureFlag) {
        return isFeatureEnabledonAccount.value(
          accountId.value,
          item.featureFlag
        );
      }
      return true;
    })
    .map(item => ({ ...item, label: t(item.labelKey) }))
);
</script>

<template>
  <div
    class="flex sm:hidden justify-around items-start w-full h-16 bg-n-background border-t border-n-weak"
  >
    <router-link
      v-for="menuItem in menuItems"
      :key="menuItem.toStateName"
      v-slot="{ isActive, href, navigate }"
      :to="menuItem.toState"
      custom
    >
      <a
        :href="href"
        :aria-label="menuItem.label"
        class="flex items-center justify-center my-2 size-10 rounded-lg"
        :class="isActive ? 'text-n-brand bg-n-alpha-2' : 'text-n-slate-11'"
        @click="navigate"
      >
        <Icon :icon="menuItem.icon" class="size-5" />
      </a>
    </router-link>

    <div class="my-2">
      <SidebarProfileMenu
        compact
        @toggle-accounts="emit('toggleAccounts')"
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </div>
  </div>
</template>
